import React, { FC, useContext, useEffect } from 'react';
import { AllieWizard, useWizardProgress, useWizardContext } from '../../../components/Wizard';
import {
  EfficiencyWizardContainer,
  StyledCancelButton,
  StyledHeaderContainer,
  StyledTitleContainer,
} from './EfficiencyWizard.styles';
import { FormContextProps } from './EfficiencyContext';
import { isTablet } from '../../../common/utils';
import { AllieText } from '../../../components/Text/Text';
const { ButtonNext, ButtonPrev, Pages, ButtonCancel, ButtonConfirm } = AllieWizard;

export interface EfficiencyWizardProps {
  className?: string;
  labels: {
    nextButton: string;
    prevButton: string;
    cancelButton: string;
    confirmButton: string;
    closeButton?: string;
    title: string;
    subtitle?: string;
    step0?: { title: string };
    step1?: { title: string; subtitle: string };
    step2?: { title: string; subtitle: string };
  };
  pages: (JSX.Element | null)[];
  isCreate?: boolean;
  context: React.Context<FormContextProps>;
  initialContextWizardState?: {
    steps: number;
    activePageIndex: number;
  };
  hideNextInPages?: number[];
  onPageChange?: (pageIndex: number) => void;
}

export interface WizardActionProps {
  onCancel: () => void;
  onConfirm: () => void;
  onCloseHeader?: () => void;
}

const namespace = 'efficiency-wizard';

export const EfficiencyWizard: FC<EfficiencyWizardProps & WizardActionProps> = ({
  className,
  labels,
  pages,
  onCancel,
  onConfirm,
  onCloseHeader,
  onPageChange,
  context,
  initialContextWizardState,
  hideNextInPages = [],
}) => {
  const { stepValid } = useContext(context) as FormContextProps;

  const WizardSteps = () => {
    const { setSteps } = useWizardContext();
    useEffect(() => {
      const pageCount = pages.filter((page) => page !== null).length;
      setSteps(pageCount);
    }, [pages]);
    return <></>;
  };

  const PageChangeNotifier = () => {
    const { activePageIndex } = useWizardContext();
    useEffect(() => {
      onPageChange?.(activePageIndex);
    }, [activePageIndex]);
    return null;
  };

  const Header = () => {
    const { activePageIndex } = useWizardContext();
    const title =
      activePageIndex === 0
        ? labels.step0?.title
        : activePageIndex !== 2
          ? labels.step1?.title
          : labels.step2?.title;

    return (
      <div className={`${namespace}__header`}>
        <AllieText color="neutral-90" type="h3" style={{ marginBottom: '36px' }}>
          {title}
        </AllieText>
        {labels.subtitle && (
          <AllieText color="neutral-90" type="body2">
            {labels.subtitle}
          </AllieText>
        )}
      </div>
    );
  };

  const Subtitle = () => {
    const { activePageIndex } = useWizardContext();

    return (
      <AllieText color="primary-50">
        {activePageIndex !== 2 ? labels.step1?.subtitle : labels.step2?.subtitle}
      </AllieText>
    );
  };

  const FooterLeftButton = () => {
    const { activePageIndex, goPrevPage } = useWizardContext();
    if (onCloseHeader && activePageIndex > 0) {
      return (
        <StyledCancelButton
          className={`${namespace}__buttons__button-prev`}
          allieSize={isTablet ? 'normal' : 'small'}
          corner="rounded-edges"
          onclick={goPrevPage}
        >
          {labels.prevButton}
        </StyledCancelButton>
      );
    }
    return (
      <ButtonCancel
        onclick={onCancel}
        className={`${namespace}__buttons__button-cancel`}
        allieSize={isTablet ? 'normal' : 'small'}
      >
        {labels.cancelButton}
      </ButtonCancel>
    );
  };

  const CustomButtonNext = () => {
    const { activePageIndex } = useWizardContext();
    const { currentIndex } = useWizardProgress();
    const isHidden = hideNextInPages.includes(activePageIndex);
    return isHidden ? null : (
      <ButtonNext
        data-testid="next-button"
        className={`${namespace}__buttons__button-next`}
        disabled={stepValid[`step${currentIndex}`] === false}
        allieSize={isTablet ? 'normal' : 'small'}
      >
        {labels.nextButton}
      </ButtonNext>
    );
  };

  const CustomButtonConfirm = () => {
    const { currentIndex: localCurrentIndex } = useWizardProgress();

    return (
      <ButtonConfirm
        data-testid="confirm-button"
        onclick={onConfirm}
        className={`${namespace}__buttons__button-confirm`}
        disabled={stepValid[`step${localCurrentIndex}`] === false}
        allieSize={isTablet ? 'normal' : 'small'}
      >
        {labels.confirmButton}
      </ButtonConfirm>
    );
  };

  return (
    <EfficiencyWizardContainer>
      <AllieWizard
        className={`${namespace} ${className}`}
        initialContextState={initialContextWizardState}
      >
        {!onCloseHeader && (
          <StyledHeaderContainer>
            <Subtitle />{' '}
            <StyledTitleContainer>
              <ButtonPrev
                className={`${namespace}__buttons__button-prev`}
                allieSize={isTablet ? 'normal' : 'small'}
              ></ButtonPrev>
              <Header />
            </StyledTitleContainer>
          </StyledHeaderContainer>
        )}

        <Pages
          className={`${namespace}__pages`}
          style={onCloseHeader ? { padding: '1.25rem 1.75rem' } : undefined}
        >
          {pages}
        </Pages>
        <div className={`${namespace}__footer`}>
          <div className={`${namespace}__buttons`}>
            <FooterLeftButton />
            <CustomButtonNext />
            <CustomButtonConfirm />
          </div>
        </div>
        <WizardSteps />
        {onPageChange && <PageChangeNotifier />}
      </AllieWizard>
    </EfficiencyWizardContainer>
  );
};
